<template>
  <section class="sale-customers-tab">
    <SaleCustomersAddModal
      :show="isAddCustomersModalOpen"
      :submitting="isAddingCustomers"
      @close="closeAddCustomersModal"
      @submit="submitAddCustomers"
    />

    <ConfirmActionModal
      :show="isDeleteConfirmModalOpen"
      :title="text.detail.customersTab.confirm.delete.title"
      :subtitle="text.detail.customersTab.confirm.delete.subtitle"
      :message="deleteConfirmMessage"
      :confirm-text="text.detail.customersTab.confirm.delete.confirm"
      @cancel="closeDeleteConfirmModal"
      @confirm="confirmDeleteCustomer"
    />

    <ConfirmActionModal
      :show="isClearConfirmModalOpen"
      :title="text.detail.customersTab.confirm.clear.title"
      :subtitle="text.detail.customersTab.confirm.clear.subtitle"
      :message="clearConfirmMessage"
      :confirm-text="text.detail.customersTab.confirm.clear.confirm"
      @cancel="closeClearConfirmModal"
      @confirm="confirmClearCustomers"
    />

    <SaleCustomersSummary
      :summary="formattedSummary"
      :can-manage-customers="canManageCustomers"
      @clear="openClearConfirmModal"
      @new-customer="handleNewCustomer"
    />

    <SaleCustomersToolbar
      :search="search"
      :plans-filter="plansFilter"
      @update:search="search = $event"
      @update:plansFilter="plansFilter = $event"
    />

    <SaleCustomersTable
      :rows="sortedRows"
      :loading="loading"
      :sort-field="customersSort.field"
      :sort-direction="customersSort.direction"
      :can-manage-customers="canManageCustomers"
      @view-detail="handleViewDetail"
      @delete-customer="handleDeleteCustomer"
      @export-pdf="handleExportPdf"
      @export-excel="handleExportExcel"
      @sort-change="handleSortChange"
    />
  </section>
</template>

<script>
import SaleCustomersSummary from '@/components/salesMultiCustomer/customers/SaleCustomersSummary.vue';
import SaleCustomersToolbar from '@/components/salesMultiCustomer/customers/SaleCustomersToolbar.vue';
import SaleCustomersTable from '@/components/salesMultiCustomer/customers/SaleCustomersTable.vue';
import SaleCustomersAddModal from '@/components/salesMultiCustomer/customers/SaleCustomersAddModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { buildAccountStatementFileName, downloadBlobFile } from '@/utils/fileDownload';
import { formatCurrency } from '@/utils/numberFormat';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';

const { catalog: text } = useDomainText('sales');

// TEMPORAL: mientras los permisos por feature estan desactivados en backend,
// estas acciones se restringen por rol para reducir cambios de comportamiento.
// Remover este gate y volver a capacidades/permisos cuando se reactive la logica final.
const TEMP_ADMIN_ONLY_CUSTOMER_ACTIONS = true;

export default {
  name: 'SaleCustomersTab',
  components: {
    SaleCustomersSummary,
    SaleCustomersToolbar,
    SaleCustomersTable,
    SaleCustomersAddModal,
    ConfirmActionModal,
  },
  props: {
    sale: {
      type: Object,
      default: null,
    },
  },
  emits: ['open-payment-plan-for-customer'],
  data() {
    return {
      text,
      search: '',
      plansFilter: 'all',
      loading: false,
      rows: [],
      summary: {
        customers: 0,
        average_per_customer: 0,
        top_income_per_customer: 0,
      },
      isDeleteConfirmModalOpen: false,
      deletingCustomer: null,
      isClearConfirmModalOpen: false,
      isAddCustomersModalOpen: false,
      isAddingCustomers: false,
      customersSort: {
        field: 'customer',
        direction: 'asc',
      },
    };
  },
  computed: {
    saleId() {
      return Number(this.sale?.id || this.sale?.sale_id || 0);
    },
    filteredRows() {
      const query = String(this.search || '').trim().toLowerCase();

      return this.rows.filter((row) => {
        const plansCount = Number(row?.plans_count || 0);
        if (this.plansFilter === 'with' && plansCount <= 0) {
          return false;
        }

        if (this.plansFilter === 'without' && plansCount > 0) {
          return false;
        }

        if (query === '') {
          return true;
        }

        return [row?.name, row?.main_contact_name, row?.email]
          .some((value) => String(value || '').toLowerCase().includes(query));
      });
    },
    sortedRows() {
      return sortRows(this.filteredRows, this.customersSort, {
        customer: (row) => String(row?.name || '').trim().toLowerCase(),
        plans_total: (row) => Number(row?.plans_total ?? row?.income_total ?? 0),
        billed_total: (row) => Number(row?.billed_total ?? 0),
        paid_total: (row) => Number(row?.paid_total ?? 0),
      });
    },
    formattedSummary() {
      return {
        customers: String(Number(this.summary?.customers || 0)),
        averagePerCustomer: formatCurrency(this.summary?.average_per_customer || 0),
        topIncomePerCustomer: formatCurrency(this.summary?.top_income_per_customer || 0),
      };
    },
    deleteConfirmMessage() {
      const customerName = String(this.deletingCustomer?.name || '').trim() || this.text.detail.customersTab.confirm.delete.customerFallback;
      return this.text.detail.customersTab.confirm.delete.message.replace('{customerName}', customerName);
    },
    clearConfirmMessage() {
      return this.text.detail.customersTab.confirm.clear.message;
    },
    canManageCustomers() {
      if (!TEMP_ADMIN_ONLY_CUSTOMER_ACTIONS) {
        return true;
      }

      const normalizedRole = this.resolveCurrentUserRoleNormalized();
      return normalizedRole === 'administrador' || normalizedRole === 'administrator' || normalizedRole === 'admin';
    },
  },
  watch: {
    saleId: {
      immediate: true,
      handler() {
        this.fetchData();
      },
    },
  },
  methods: {
    async fetchData() {
      if (this.saleId <= 0) {
        this.rows = [];
        this.summary = {
          customers: 0,
          average_per_customer: 0,
          top_income_per_customer: 0,
        };
        return;
      }

      this.loading = true;
      try {
        const [customersResponse, metricsResponse] = await Promise.all([
          salesMultiCustomerService.customers(this.saleId),
          salesMultiCustomerService.customersMetrics(this.saleId),
        ]);

        this.rows = extractCollection(customersResponse);
        const metrics = extractPayload(metricsResponse);
        this.summary = {
          customers: Number(metrics?.customers || 0),
          average_per_customer: Number(metrics?.average_per_customer || 0),
          top_income_per_customer: Number(metrics?.top_income_per_customer || 0),
        };
      } catch (error) {
        this.rows = [];
        this.summary = {
          customers: 0,
          average_per_customer: 0,
          top_income_per_customer: 0,
        };
      } finally {
        this.loading = false;
      }
    },
    openClearConfirmModal() {
      if (!this.canManageCustomers) {
        return;
      }

      this.isClearConfirmModalOpen = true;
    },
    closeClearConfirmModal() {
      this.isClearConfirmModalOpen = false;
    },
    async confirmClearCustomers() {
      const saleId = Number(this.saleId || 0);
      if (saleId <= 0) {
        this.closeClearConfirmModal();
        return;
      }

      try {
        const response = await salesMultiCustomerService.clearCustomersWithoutPlans(saleId);
        const apiErrorMessage = extractApiErrorMessage(
          response,
          this.text.detail.customersTab.toasts.clearError,
        );

        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.detail.customersTab.toasts.clearSuccess));
        this.closeClearConfirmModal();
        this.search = '';
        this.plansFilter = 'all';
        await this.fetchData();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.detail.customersTab.toasts.clearError);
      }
    },
    handleNewCustomer() {
      if (!this.canManageCustomers) {
        return;
      }

      this.isAddCustomersModalOpen = true;
    },
    closeAddCustomersModal() {
      if (this.isAddingCustomers) {
        return;
      }

      this.isAddCustomersModalOpen = false;
    },
    async submitAddCustomers(payload) {
      const saleId = Number(this.saleId || 0);
      if (saleId <= 0 || this.isAddingCustomers) {
        return;
      }

      this.isAddingCustomers = true;

      try {
        const response = await salesMultiCustomerService.addCustomers(saleId, payload || {});
        const apiErrorMessage = extractApiErrorMessage(
          response,
          this.text.detail.customersTab.toasts.addError,
        );

        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.detail.customersTab.toasts.addSuccess));
        this.isAddCustomersModalOpen = false;
        await this.fetchData();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.detail.customersTab.toasts.addError);
      } finally {
        this.isAddingCustomers = false;
      }
    },
    handleViewDetail(row) {
      const customerId = Number(row?.id || 0);
      if (customerId <= 0) {
        return;
      }

      this.$emit('open-payment-plan-for-customer', customerId);
    },
    handleSortChange(payload = {}) {
      const nextSort = resolveSortChange(this.customersSort, payload, [
        'customer',
        'plans_total',
        'billed_total',
        'paid_total',
      ]);

      if (
        nextSort.field === this.customersSort.field
        && nextSort.direction === this.customersSort.direction
      ) {
        return;
      }

      this.customersSort = nextSort;
    },
    handleDeleteCustomer(row) {
      if (!this.canManageCustomers) {
        return;
      }

      const customerId = Number(row?.id || 0);
      if (customerId <= 0) {
        return;
      }

      this.deletingCustomer = row;
      this.isDeleteConfirmModalOpen = true;
    },
    closeDeleteConfirmModal() {
      this.isDeleteConfirmModalOpen = false;
      this.deletingCustomer = null;
    },
    async confirmDeleteCustomer() {
      const saleId = Number(this.saleId || 0);
      const customerId = Number(this.deletingCustomer?.id || 0);

      if (saleId <= 0 || customerId <= 0) {
        this.closeDeleteConfirmModal();
        return;
      }

      try {
        const response = await salesMultiCustomerService.deleteCustomer(saleId, customerId);
        const apiErrorMessage = extractApiErrorMessage(
          response,
          this.text.detail.customersTab.toasts.deleteError,
        );

        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.detail.customersTab.toasts.deleteSuccess));
        this.closeDeleteConfirmModal();
        await this.fetchData();
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.detail.customersTab.toasts.deleteError);
      }
    },
    handleExportPdf(row) {
      const saleId = Number(this.saleId || 0);
      const customerId = Number(row?.id || 0);

      if (saleId <= 0 || customerId <= 0) {
        return;
      }

      salesMultiCustomerService.customerAccountStatementPdf(saleId, customerId)
        .then((response) => {
          const fileName = buildAccountStatementFileName(row?.name, 'pdf');
          downloadBlobFile(response, fileName, 'application/pdf');
        })
        .catch((error) => {
          this.$toast?.error(error?.response?.data?.message || error?.message || this.text.detail.customersTab.toasts.exportPdfError);
        });
    },
    async handleExportExcel(row) {
      const saleId = Number(this.saleId || 0);
      const customerId = Number(row?.id || 0);

      if (saleId <= 0 || customerId <= 0) {
        return;
      }

      try {
        const response = await salesMultiCustomerService.customerAccountStatementExcel(saleId, customerId);
        const fileName = buildAccountStatementFileName(row?.name, 'xlsx');
        downloadBlobFile(response, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || this.text.detail.customersTab.toasts.exportExcelError);
      }
    },
    resolveCurrentUserRoleNormalized() {
      const rawUser = localStorage.getItem('user');
      if (!rawUser || rawUser === 'undefined') {
        return '';
      }

      try {
        const parsed = JSON.parse(rawUser) || {};
        const rawRole = String(parsed?.role || parsed?.rol || '').trim().toLowerCase();
        return rawRole.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      } catch (error) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
.sale-customers-tab {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
